import { useEffect } from 'react';

import type { TFundraiserState } from '@feathr/blackbox';
import { EFormState } from '@feathr/blackbox';
import { useAccount, useStore } from '@feathr/extender/state';
import { logUserEvents } from '@feathr/hooks';

/**
 * Tracks Appcues events for the Fundraise page:
 * - `connected_stripe`: Fires once per account when Stripe is connected
 * - `processed_first_donation`: Fires once per account when any published form has donations
 *
 * Uses localStorage to ensure events fire only once per account.
 */
export function useFundraiseTracking(stripeState: TFundraiserState | undefined): void {
  const account = useAccount();
  const { DonationForms } = useStore();

  useEffect(() => {
    const storageKey = `appcues_stripe_connected_${account.id}`;
    const alreadyTracked = localStorage.getItem(storageKey);

    if (stripeState === 'connected' && !alreadyTracked) {
      logUserEvents({ connected_stripe: null });
      localStorage.setItem(storageKey, 'true');
    }
  }, [stripeState, account.id]);

  useEffect(() => {
    const storageKey = `appcues_first_donation_${account.id}`;
    const alreadyTracked = localStorage.getItem(storageKey);

    if (alreadyTracked) {
      return;
    }

    const forms = DonationForms.list({ filters: { state: EFormState.Published } });
    if (!forms.isPending) {
      const hasAnyDonation = forms.models.some(
        (form) => (form.get('stats_cache')?.donations_count ?? 0) > 0,
      );
      if (hasAnyDonation) {
        logUserEvents({ processed_first_donation: null });
        localStorage.setItem(storageKey, 'true');
      }
    }
  }, [DonationForms, account.id]);
}
